import React, { useEffect, useState } from "react";

import {
  ITaskTemplateInputDefinition,
  TaskTemplateInputFromSubgroup,
} from "api/model/airflow-task/task-template/TaskTemplateInfo";
import { TaskTemplateInputSubType, TaskTemplateInputType } from "api/model/enums";
import DatasetUnitSelector from "components/basic/dropdowns/dataset-unit-selector/DatasetUnitSelector";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import { Chips } from "primereact/chips";
import { Column } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import {
  DataTable,
  DataTableRowEditCompleteEvent,
  DataTableRowReorderEvent,
  DataTableValueArray,
} from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import "./TemplateEditor.css";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import { createObject, handleMiniDialogCancel } from "./TemplateEditorUtils";

type InputGroupedDefinitionsSubFormsListDialogProps = {
  subForm: TaskTemplateInputFromSubgroup;
  index: number;
  onSubFormChange: (newSubForm: TaskTemplateInputFromSubgroup, index: number) => void;
};

const InputGroupedDefinitionsSubFormsListDialog: React.FC<InputGroupedDefinitionsSubFormsListDialogProps> = ({
  subForm,
  index,
  onSubFormChange,
}) => {
  const initialSubform = subForm;
  const [localSubForm, setLocalSubForm] = useState<TaskTemplateInputFromSubgroup>(subForm);
  const [dialog, setDialog] = useState<boolean>(false);
  const [selectedInput, setSelectedInput] = useState<ITaskTemplateInputDefinition | null>(null);
  const [miniDialog, setMiniDialog] = useState<boolean>(false);
  const [newInputId, setNewInputId] = useState<string>("");
  const [newInputTitle, setNewInputTitle] = useState<string>("");
  const [newInputType, setNewInputType] = useState<TaskTemplateInputType>(
    TaskTemplateInputType.INPUT_NAMED_DATASET
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tempId, setTempId] = useState<string>("");
  const taskTemplateInputType = Object.keys(TaskTemplateInputType).map(key => ({
    label: TaskTemplateInputType[key as keyof typeof TaskTemplateInputType],
    value: TaskTemplateInputType[key as keyof typeof TaskTemplateInputType],
  }));

  const taskTemplateInputSubType = Object.keys(TaskTemplateInputSubType).map(key => ({
    label: TaskTemplateInputSubType[key as keyof typeof TaskTemplateInputSubType],
    value: TaskTemplateInputSubType[key as keyof typeof TaskTemplateInputSubType],
  }));

  useEffect(() => {
    setLocalSubForm(subForm);
  }, [subForm]);

  const saveSelectedInput = () => {
    if (!selectedInput) return;

    setLocalSubForm(prevSubForm => {
      const updatedSubForm = { ...prevSubForm };
      const inputIndex = updatedSubForm.inputs?.findIndex(input => input.id === selectedInput.id);

      if (inputIndex !== undefined && inputIndex !== -1 && updatedSubForm.inputs) {
        updatedSubForm.inputs[inputIndex] = selectedInput;
      } else {
        updatedSubForm.inputs = [...(updatedSubForm.inputs || []), selectedInput];
      }

      return updatedSubForm;
    });
  };

  const cancelDialog = () => {
    setSelectedInput(null);
    setLocalSubForm(initialSubform);
    setDialog(false);
  };

  const saveDialog = () => {
    saveSelectedInput();
    onSubFormChange(localSubForm, index);
    setSelectedInput(null);
    setDialog(false);
  };

  const handleMiniDialogSubmit = () => {
    if (localSubForm.inputs?.some(input => input.id === newInputId)) {
      easasAlert({
        severity: "error",
        summary: T("error.input-id-already-exists-please-choose-a-different-id", {
          newId: newInputId,
        }),
      });
      return;
    }
    const newInput: ITaskTemplateInputDefinition = {
      id: newInputId,
      title: newInputTitle,
      type: newInputType,
      width: 12,
      options: {
        subType: TaskTemplateInputSubType.STRING,
        unit: "",
        selectOptions: [],
      },
    };
    if (newInputType === TaskTemplateInputType.COMMENT && newInput.options) {
      newInput.options.customConfig = { content: "" };
    }
    setLocalSubForm(prevSubForm => {
      return {
        ...prevSubForm,
        inputs: [...(prevSubForm.inputs || []), newInput],
      };
    });
    setMiniDialog(false);
    setNewInputId("");
    setNewInputTitle("");
    setNewInputType(TaskTemplateInputType.INPUT_NAMED_DATASET);
  };

  const handleTypeChange = (value: string) => {
    if (!selectedInput) return;
    const updatedInput = { ...selectedInput, type: value as TaskTemplateInputType };
    if (!updatedInput.options) {
      updatedInput.options = { subType: TaskTemplateInputSubType.STRING };
    }

    if (value === TaskTemplateInputType.COMMENT && updatedInput.options?.subType) {
      updatedInput.options.customConfig = { content: "" };
      delete updatedInput.options.subType;
    } else {
      updatedInput.options = {
        ...updatedInput.options,
        subType: TaskTemplateInputSubType.STRING,
        selectOptions: [],
        customConfig: {},
      };
    }
    setSelectedInput(updatedInput);
  };

  const handleSubTypeChange = (value: string) => {
    if (!selectedInput) return;
    const updatedInput = {
      ...selectedInput,
      options: {
        ...selectedInput.options,
        subType: value as TaskTemplateInputSubType | undefined,
      },
    };
    if (value === TaskTemplateInputSubType.XY_VALUE_MATRIX) {
      updatedInput.options.customConfig = { xLabel: "", yLabel: "" };
    } else if (value === TaskTemplateInputSubType.STRING) {
      updatedInput.options.selectOptions = [];
    } else {
      delete updatedInput.options.customConfig;
      delete updatedInput.options.selectOptions;
    }
    setSelectedInput(updatedInput);
  };

  const handleUnitChange = (value: string) => {
    if (!selectedInput) return;
    const updatedInput = {
      ...selectedInput,
      options: {
        ...selectedInput.options,
        unit: value,
      },
    };
    setSelectedInput(updatedInput);
  };

  const confirmOnDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, inputIndex: number) => {
    confirmPopup({
      target: e.currentTarget,
      message: T("confirm-popup.are-you-sure-you-want-to-delete-this-input"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        setLocalSubForm(prevSubForm => {
          const updatedSubForm = {
            ...prevSubForm,
            inputs: prevSubForm.inputs?.filter((_, i) => i !== inputIndex) || [],
          };
          return updatedSubForm;
        });
        setSelectedInput(null);
      },
    });
  };

  const onRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const { newData } = e;
    setSelectedInput(newData as ITaskTemplateInputDefinition);
  };

  const onRowReorder = (e: DataTableRowReorderEvent<DataTableValueArray>) => {
    setLocalSubForm(prevSubForm => ({ ...prevSubForm, inputs: e.value as ITaskTemplateInputDefinition[] }));
  };

  const dialogFooterContent = () => (
    <div>
      <Button
        label={T("word.cancel")}
        icon="pi pi-times"
        onClick={cancelDialog}
        className="component-editor-button icon-red"
      />
      <Button
        label={T("action.save")}
        icon="pi pi-check"
        onClick={saveDialog}
        className="component-editor-button icon-green"
      />
    </div>
  );

  const renderIdInput = (
    rowData: ITaskTemplateInputDefinition,
    isTable: boolean,
    confirmOnChange?: (value: string) => void
  ) => (
    <div>
      {isTable ? (
        <div>{editingId === rowData.id ? tempId : rowData.id}</div>
      ) : (
        <div>
          <InputText
            placeholder="Id"
            value={editingId === rowData.id ? tempId : rowData.id}
            onChange={e => {
              setEditingId(rowData.id);
              setTempId(e.target.value);
            }}
            onClick={() => setSelectedInput(rowData)}
            className="p-inputtext-sm"
          />
          {selectedInput && editingId === selectedInput.id && confirmOnChange && (
            <Button
              label={T("action.update")}
              size="small"
              className="p-button-primary w-5rem"
              onClick={() => confirmOnChange(tempId)}
            />
          )}
        </div>
      )}
    </div>
  );

  const renderTitleInput = (rowData: ITaskTemplateInputDefinition) => (
    <div>{selectedInput?.id === rowData.id ? selectedInput.title : rowData.title}</div>
  );

  const renderTypeInput = (rowData: ITaskTemplateInputDefinition) => (
    <div>{selectedInput?.id === rowData.id ? selectedInput.type : rowData.type}</div>
  );

  const renderDeleteButton = (rowData: ITaskTemplateInputDefinition, options: any) => (
    <Button
      icon="pi pi-trash"
      className="p-button-rounded p-button-text p-button-danger my-auto"
      onClick={e => confirmOnDelete(e, options.rowIndex)}
    />
  );

  const renderWidthInput = (rowData: ITaskTemplateInputDefinition) => (
    <div>{selectedInput?.id === rowData.id ? selectedInput.width : rowData.width}</div>
  );

  const handleXYValueMatrixUnitChange = (value: string, propertyName: string) => {
    if (!selectedInput) return;
    const updatedInput = {
      ...selectedInput,
      options: {
        ...selectedInput.options,
        customConfig: {
          ...selectedInput.options?.customConfig,
          [propertyName]: value,
        },
      },
    };
    setSelectedInput(updatedInput);
  };

  const renderDuplicateButton = (rowData: ITaskTemplateInputDefinition) => (
    <Button
      icon="pi pi-copy"
      className="p-button-rounded p-button-text p-button-primary my-auto"
      onClick={() => handleDuplicateInput(rowData)}
    />
  );

  const handleDuplicateInput = (input: ITaskTemplateInputDefinition) => {
    let newId = `${input.id}_1`;

    if (localSubForm.inputs) {
      for (let i = 0; i < localSubForm.inputs.length; i++) {
        if (localSubForm.inputs[i].id === newId) {
          newId = `${newId}_1`;
          i = 0;
        }
      }
      setLocalSubForm(prevSubForm => {
        const updatedSubForm = {
          ...prevSubForm,
          inputs: [...(prevSubForm.inputs || []), { ...selectedInput!, id: newId }],
        };
        return updatedSubForm;
      });
    }
  };

  const confirmOnIdChange = (newId: string) => {
    easasConfirmDialog({
      header: T("action.confirm-action"),
      message: T("confirm-popup.are-you-sure-you-want-to-change-the-id"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-success",
      confirmedCallback: () => {
        if (selectedInput && !localSubForm.inputs?.find(input => input.id === newId)) {
          setLocalSubForm(prevSubForm => {
            const updatedSubForm = { ...prevSubForm };
            const inputIndex = updatedSubForm.inputs?.findIndex(input => input.id === selectedInput.id);
            if (inputIndex !== undefined && inputIndex !== -1 && updatedSubForm.inputs) {
              updatedSubForm.inputs[inputIndex] = { ...selectedInput, id: newId };
            }
            return updatedSubForm;
          });
          setSelectedInput(null);
          setEditingId(null);
          easasAlert({
            severity: "success",
            summary: T("alert.id-changed-to", { newId }),
          });
          setTempId("");
        } else {
          easasAlert({
            severity: "error",
            summary: T("error.input-id-already-exists-please-choose-a-different-id", { newId }),
          });
        }
      },
    });
  };

  const handleDialogClose = () => {
    setDialog(false);
    setSelectedInput(null);
    setEditingId(null);
    setTempId("");
  };

  const handleCreateObject = (type: TaskTemplateInputType) => {
    createObject(setMiniDialog);
    setNewInputType(type);
  };

  return (
    <>
      <Button icon="pi pi-cog" className="p-button-text" size="small" onClick={() => setDialog(true)} />
      <Dialog
        modal={false}
        visible={dialog}
        blockScroll
        onHide={handleDialogClose}
        header={`"${localSubForm.header || T("word.no-name")}" ${T("word.inputs-management")}`}
        style={{ width: "min(100%, 1300px)" }}
        footer={dialogFooterContent}
      >
        <div className="flex flex-row gap-2 mb-2">
          <Button
            className="component-editor-button icon-green"
            label={T("word.input")}
            icon="pi pi-plus"
            onClick={() => handleCreateObject(TaskTemplateInputType.INPUT_NAMED_DATASET)}
          />
          <Button
            className="component-editor-button icon-green"
            label={T("word.output")}
            icon="pi pi-plus"
            onClick={() => handleCreateObject(TaskTemplateInputType.OUTPUT_NAMED_DATASET)}
          />
          <Button
            className="component-editor-button icon-green"
            label={T("word.constant")}
            icon="pi pi-plus"
            onClick={() => handleCreateObject(TaskTemplateInputType.CONSTANT)}
          />
        </div>
        <DataTable
          value={localSubForm.inputs}
          editMode="cell"
          dataKey="id"
          selection={selectedInput}
          onSelectionChange={e => {
            if (selectedInput?.id === e.value?.id) {
              setSelectedInput(null);
            } else {
              saveSelectedInput();
              setSelectedInput(e.value as ITaskTemplateInputDefinition);
            }
          }}
          metaKeySelection={false}
          onRowEditComplete={onRowEditComplete}
          reorderableRows
          onRowReorder={onRowReorder}
          responsiveLayout="scroll"
          scrollHeight="300px"
          size="small"
          className="p-0"
        >
          <Column selectionMode="single" style={{ width: "2%", padding: "0.3rem" }} />
          <Column
            field="id"
            header="Id"
            style={{ padding: 0, width: "30%" }}
            body={rowData => renderIdInput(rowData, true)}
          />
          <Column
            field="title"
            header={T("word.title")}
            style={{ padding: 0, width: "30%" }}
            body={rowData => renderTitleInput(rowData)}
          />
          <Column
            field="type"
            header={T("word.type")}
            style={{ padding: 0, width: "25%" }}
            body={rowData => renderTypeInput(rowData)}
          />
          <Column
            field="width"
            header={T("word.width")}
            style={{ padding: 0, width: "10%" }}
            body={rowData => renderWidthInput(rowData)}
          />
          <Column
            style={{ padding: 0, width: "0" }}
            body={(rowData, options) => renderDeleteButton(rowData, options)}
          />
          <Column style={{ padding: 0, width: "0" }} body={rowData => renderDuplicateButton(rowData)} />
          <Column rowReorder style={{ padding: 0, textAlign: "center", width: "5%" }} />
        </DataTable>
        <div className="subform-container mt-4">
          <div className="selected-input-details">
            <h5>{T("label.selected-input-details")}</h5>
            {selectedInput && (
              <div className="flex flex-row">
                <div className="flex flex-column gap-2 p-col-2">
                  <label>Id:</label>
                  {renderIdInput(selectedInput, false, confirmOnIdChange)}
                </div>
                <div className="flex flex-column gap-2 p-col-2">
                  <label>{T("word.title")}:</label>
                  <InputText
                    placeholder={T("word.title")}
                    value={selectedInput.title || ""}
                    onChange={e => {
                      const updatedInput = { ...selectedInput, title: e.target.value };
                      setSelectedInput(updatedInput);
                    }}
                    className="p-inputtext-sm"
                  />
                </div>
                <div className="flex flex-column p-col-2">
                  <label className="mb-2">{T("word.type")}:</label>
                  <Dropdown
                    value={selectedInput.type}
                    options={taskTemplateInputType}
                    filter
                    showClear
                    onChange={e => handleTypeChange(e.value as string)}
                    optionLabel="label"
                    placeholder={T("action.select-a-type")}
                    className="p-inputtext-sm"
                  />
                </div>
                <div className="flex flex-column gap-2 p-col-1">
                  <label>{T("word.width")}:</label>
                  <InputText
                    placeholder={T("word.width")}
                    value={selectedInput.width?.toString() || ""}
                    onChange={e => {
                      const updatedInput = { ...selectedInput, width: Number(e.target.value) };
                      setSelectedInput(updatedInput);
                    }}
                    className="p-inputtext-sm"
                  />
                </div>
                {selectedInput.type === TaskTemplateInputType.COMMENT &&
                  selectedInput.options?.customConfig && (
                    <div className="flex flex-column gap-2 p-col-2 h-100">
                      <label>{T("word.content")} :</label>
                      <InputText
                        placeholder={T("word.content")}
                        value={selectedInput.options.customConfig.content || ""}
                        onChange={e => {
                          const updatedInput = {
                            ...selectedInput,
                            options: {
                              ...selectedInput.options,
                              customConfig: {
                                ...selectedInput.options?.customConfig,
                                content: e.target.value,
                              },
                            },
                          };
                          setSelectedInput(updatedInput);
                        }}
                        className="p-inputtext-sm"
                      />
                    </div>
                  )}
                {selectedInput.type === TaskTemplateInputType.CONSTANT && (
                  <div>
                    <div className="flex flex-row">
                      <div className="flex flex-column gap-2 p-col-full m-3">
                        <label>{T("word.subtype")}:</label>
                        <Dropdown
                          value={selectedInput.options?.subType || ""}
                          options={taskTemplateInputSubType}
                          filter
                          showClear
                          onChange={e => handleSubTypeChange(e.value as string)}
                          optionLabel="label"
                          placeholder={T("action.select-a-type")}
                          className="p-inputtext-sm w-12rem"
                        />
                      </div>
                      {selectedInput.options &&
                        selectedInput.options.subType === TaskTemplateInputSubType.NUMBER && (
                          <>
                            <div className="flex flex-column gap-2 m-3">
                              <label>{T("config.decimal-places")}</label>
                              <InputNumber
                                value={selectedInput.options.decimalPlaces}
                                onValueChange={e => {
                                  const updatedInput = {
                                    ...selectedInput,
                                    options: {
                                      ...selectedInput.options,
                                      decimalPlaces: Number(e.value),
                                    },
                                  };
                                  setSelectedInput(updatedInput);
                                }}
                                className="p-inputtext-sm"
                              />
                            </div>
                            <div className="flex flex-column gap-2 m-3">
                              <label>{T("label.default-value")}</label>
                              <InputNumber
                                value={selectedInput.options.defaultValue}
                                onValueChange={e => {
                                  const updatedInput = {
                                    ...selectedInput,
                                    options: {
                                      ...selectedInput.options,
                                      defaultValue: e.value,
                                    },
                                  };
                                  setSelectedInput(updatedInput);
                                }}
                                useGrouping={false}
                                className="p-inputtext-sm"
                              />
                            </div>
                          </>
                        )}
                      {selectedInput.options &&
                        selectedInput.options.subType === TaskTemplateInputSubType.STRING && (
                          <div className="flex flex-column gap-2 m-3">
                            <label>{T("label.default-value")}</label>
                            <InputText
                              value={selectedInput.options.defaultValue}
                              onChange={e => {
                                const updatedInput = {
                                  ...selectedInput,
                                  options: {
                                    ...selectedInput.options,
                                    defaultValue: e.target.value,
                                  },
                                };
                                setSelectedInput(updatedInput);
                              }}
                              className="p-inputtext-sm"
                            />
                          </div>
                        )}
                    </div>

                    {selectedInput.options &&
                      selectedInput.options.subType === TaskTemplateInputSubType.STRING && (
                        <div className="flex flex-column gap-2 p-col-12">
                          <label>{T("label.selected-options")}:</label>
                          <Chips
                            value={selectedInput.options.selectOptions}
                            onChange={e => {
                              const updatedInput = {
                                ...selectedInput,
                                options: {
                                  ...selectedInput.options,
                                  selectOptions: e.target.value,
                                },
                              };
                              setSelectedInput(updatedInput as ITaskTemplateInputDefinition);
                            }}
                          />
                        </div>
                      )}
                    {selectedInput.options?.customConfig &&
                      selectedInput.options.subType === TaskTemplateInputSubType.XY_VALUE_MATRIX && (
                        <div className="custom-config-container">
                          <div className="flex flex-column">
                            <div className="flex flex-column gap-2 p-col-12">
                              <label>X {T("word.label")}:</label>
                              <InputText
                                placeholder={`X ${T("word.label")}`}
                                value={selectedInput.options.customConfig.xLabel || ""}
                                onChange={e => {
                                  const updatedInput = {
                                    ...selectedInput,
                                    options: {
                                      ...selectedInput.options,
                                      customConfig: {
                                        ...selectedInput.options?.customConfig,
                                        xLabel: e.target.value,
                                      },
                                    },
                                  };
                                  setSelectedInput(updatedInput);
                                }}
                                className="p-inputtext-sm"
                              />
                            </div>
                            <div className="flex flex-column gap-2 p-col-12">
                              <label>X {T("label.axis-unit")}</label>
                              <DatasetUnitSelector
                                className="p-inputtext-sm"
                                value={selectedInput.options.customConfig.xUnit || ""}
                                onChange={e => handleXYValueMatrixUnitChange(e.value, "xUnit")}
                              />
                            </div>
                          </div>

                          <div>
                            <div className="flex flex-column gap-2 p-col-12">
                              <label>Y {T("word.label")}:</label>
                              <InputText
                                placeholder={`Y ${T("word.label")}`}
                                value={selectedInput.options.customConfig.yLabel || ""}
                                onChange={e => {
                                  const updatedInput = {
                                    ...selectedInput,
                                    options: {
                                      ...selectedInput.options,
                                      customConfig: {
                                        ...selectedInput.options?.customConfig,
                                        yLabel: e.target.value,
                                      },
                                    },
                                  };
                                  setSelectedInput(updatedInput);
                                }}
                                className="p-inputtext-sm"
                              />
                            </div>
                            <div className="flex flex-column gap-2 p-col-12">
                              <label>Y {T("label.axis-unit")}</label>
                              <DatasetUnitSelector
                                className="p-inputtext-sm"
                                value={selectedInput.options.customConfig.yUnit || ""}
                                onChange={e => handleXYValueMatrixUnitChange(e.value, "yUnit")}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                    {selectedInput.options &&
                      selectedInput.options.subType === TaskTemplateInputSubType.NUMBER && (
                        <div className="flex gap-2">
                          <div className="flex flex-column gap-2 p-col-6">
                            <label>{T("word.unit")}:</label>
                            <DatasetUnitSelector
                              className="p-inputtext-sm"
                              value={selectedInput.options.unit || ""}
                              onChange={e => {
                                handleUnitChange(e.value);
                              }}
                            />
                          </div>
                          <div className="flex flex-column gap-2 p-col-6">
                            <label>{T("label.selected-options")}:</label>
                            <Chips
                              value={selectedInput.options.selectOptions}
                              keyfilter="num"
                              pt={{ container: { className: "w-full px-3 pt-1 pb-0" } }}
                              onChange={e => {
                                const updatedInput = {
                                  ...selectedInput,
                                  options: {
                                    ...selectedInput.options,
                                    selectOptions: e.target.value?.map(Number),
                                  },
                                };
                                setSelectedInput(updatedInput as ITaskTemplateInputDefinition);
                              }}
                            />
                          </div>
                        </div>
                      )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Dialog>
      <Dialog
        header={T("action.create-new-input")}
        blockScroll
        visible={miniDialog}
        onHide={() => setMiniDialog(false)}
        className="w-2"
        footer={
          <div>
            <Button
              label={T("word.cancel")}
              icon="pi pi-times"
              onClick={() => handleMiniDialogCancel(setMiniDialog, setNewInputId, setNewInputId)}
              className="component-editor-button icon-red"
            />
            <Button
              label={T("action.create")}
              icon="pi pi-check"
              onClick={handleMiniDialogSubmit}
              className="component-editor-button icon-green"
            />
          </div>
        }
      >
        <div className="flex flex-column gap-2 mb-3">
          <label htmlFor="newInputId">ID</label>
          <InputText
            id="newInputId"
            value={newInputId || ""}
            onChange={e => setNewInputId(e.target.value)}
            className="p-inputtext-sm"
          />
        </div>
        <div className="flex flex-column gap-2 mb-3">
          <label htmlFor="newInputTitle">{T("word.title")}</label>
          <InputText
            id="newInputTitle"
            value={newInputTitle || ""}
            onChange={e => setNewInputTitle(e.target.value)}
            className="p-inputtext-sm"
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="newInputType">{T("word.type")}</label>
          <Dropdown
            id="newInputType"
            value={newInputType || ""}
            options={taskTemplateInputType}
            filter
            showClear
            onChange={e => setNewInputType(e.value as TaskTemplateInputType)}
            optionLabel="label"
            placeholder={T("action.select-a-type")}
            className="p-inputtext-sm"
          />
        </div>
      </Dialog>
    </>
  );
};

export default InputGroupedDefinitionsSubFormsListDialog;
